import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { CustomerContext } from '../../../contexts/CustomerContext'
import PackageItem from '../../PackageItem'
import PackageCardV2 from '../../PackageCardV2'
import styles from './PackageSelection.module.sass'
import Grid from '../../Grid'
import Typography from '../../Typography'
import { Package, StudentImage } from '../../../models/_models'
import useQueryString from '../../../hooks/use-query-string'
import { routes } from '../../../routes'
import { usePoses } from '../_ElementaryMultiPose/MultiPosePackageBuilder/hooks'
import useLocalStorage from "../../../hooks/useLocalStorage";
import classes from "../Sittings/Sittings.module.sass";
import Spinner from "../../Spinner";
import prevYearsBanner from '../../../assets/prev_years.png'
import OrderPackage from "../../../models/OrderPackage";
import { Pose } from "../../../models";
import { getPreviousOrders } from "../../../api/previous_orders";
import PreviousYears from './PreviousYears';
import usePastImagesQuickOrderPricelist from '../../../hooks/usePastImagesQuickOrderPricelist';
import usePackageOptions from '../../../hooks/usePackageOptions';
import offeredOptions, { priceOf } from '../../PackageChooser/offeredOptions';
import AbsolutePosImgs from '../AbsolutePosImgs/AbsolutePosImgs';
import { artHasCompositeSlots, selectLayout, LAYOUTS } from '../AbsolutePosImgs/packageLayouts';

const CATEGORY_WEIGHTS = {
  normal: 0,
  gift: 1
};

const PURCHASED_CATEGORY_WEIGHTS = {
  gift: 0,
  normal: 1,
};

// A stable stand-in so the compositor can be called before the options load.
// AbsolutePosImgs runs hooks, so the CALL can never be conditional; an option
// with no name resolves to no layout and it returns null after its hooks have
// run. Module scope keeps the identity stable across renders.
const NO_OPTION = { name: '' };

// How much of the student a layout actually shows — the number of slots its art
// leaves for them. The tiebreak below, and the reason it is a real measure: a
// card exists to show the customer their own child in the package.
const filledSlots = (packageId, option) => {
  const layout = LAYOUTS[selectLayout(packageId, option.name)];
  return layout ? layout.filter(Boolean).length : 0;
};

// Which option's mockup stands in for the whole pricelist on its card.
//
// The dearest one we can actually DRAW: priced and sellable (offeredOptions —
// the same filter the option grid uses, so an add-on or the buy-all upsell can
// never become the card), art drawn with empty slots for the student, and a
// measured layout for that art.
//
// ⚠️ PRICE ALONE IS NOT ENOUGH, and 976 is why. Its top tier is a genuine tie:
// "Superstar" and "The Digital Package" are both featured and both $71.99, so
// the winner fell out of `offeredOptions`' stable sort — i.e. out of
// `.order('abbreviation')` in usePackageOptions. Superstar's abbreviation is
// empty and sorts last; "A" won, and the whole sports pricelist was fronted by
// a card showing one photo on a phone instead of the 13-slot Superstar mockup.
// Breaking the tie on FILLED SLOTS picks the richer mockup on purpose, and
// stops the card depending on a field nobody edits with this in mind.
//
// Null means the card renders exactly as it does now. That is the designed
// failure: see SportPackageCard.
export const heroOption = (packageId, options) => {
  const drawable = offeredOptions(options).filter((option) => (
    artHasCompositeSlots(option.imageUrl) && !!selectLayout(packageId, option.name)
  ));

  if (drawable.length === 0) return null;

  return drawable.reduce((best, option) => {
    const byPrice = (priceOf(option) || 0) - (priceOf(best) || 0);
    if (byPrice !== 0) return byPrice > 0 ? option : best;
    return filledSlots(packageId, option) > filledSlots(packageId, best) ? option : best;
  });
};

// Where a SPORT pricelist's card goes for a player who was never photographed
// on their own: their team picture, not the favorites gallery, which would ask
// them to pick a favourite out of one landscape team shot. 87 of the 134
// players in the first sportsfall2027 run were in that position.
//
// Null means "route this card the ordinary way". Exported because it is the
// whole decision and it is worth testing without mounting the screen.
//
// Every input is the SERVER's answer: `isSport` is SportSeason.sport? through
// Api::PackageResource, `sportTeamPhotoOnly` and `sportTeamPoseId` are
// StudentImage#sport_team_photo_only? -- the one implementation of the slot
// rule. No season string and no pose number is parsed here, which is the whole
// point: the sport pipeline has twice been bitten by the client answering a
// season question its own way.
//
// The pose id must actually have arrived. Without one there is nothing to put
// in the frame, and the gallery -- which lists whatever the player does have --
// stays the right page.
export const sportTeamPictureRoute = (packageItem, studentImage) => {
  if (!packageItem || !packageItem.isSport) return null;
  if (!studentImage || !studentImage.sportTeamPhotoOnly) return null;
  if (!studentImage.sportTeamPoseId) return null;

  return routes.sportsTeamPicture(packageItem.id, {
    favorites: studentImage.sportTeamPoseId,
  });
};

// Does a sport pricelist's card show the composite package mockup?
//
// OFF, and deliberately. The composite branch below had never actually run in
// production: `isSport` was not declared on the client Package model, so
// `packageItem.isSport` read undefined and every sport card fell through to the
// plain one. Declaring that attribute (needed to route the team-only player,
// PR #5693) switched this on by accident, and the card it produced -- the
// top-tier option's artwork, full bleed, with no pricelist name under it --
// is not what we want on the picker (Alex, 2026-10-12).
//
// Kept as a flag rather than deleted: heroOption, SportPackageCard and their
// tests are all still exercised, and turning the card on again is this one
// line plus a look at the result. The routing use of `isSport` is unaffected.
const SPORT_COMPOSITE_CARD = false;

// A SPORT pricelist's card: the top-tier package mockup with THIS student's
// poses composited into its prints, instead of the stock "SPORTS" graphic.
//
// ⚠️ IT GATES ON THE ART, NOT THE SEASON. Poses may be transparent cutouts, so
// a cutout laid over a slot that already holds a stock model leaves that
// model's arm, sleeve and ball showing around the student — two children in one
// print. #5306 learned this on the Buy All card and the answer was the same:
// match the FILE, fail safe, and let the card draw as designed until art with
// real placeholders is attached.
//
// That gate OPENED on 2026-10-07, when package 976's art was reissued with
// empty print placeholders over a stadium backdrop (`..._blank.png`). Seven of
// its options now have measured layouts — see the SPORTS block in
// packageLayouts.js. The other three keep self-contained art and stay bare.
//
// The gate still matters: blanking an option's art is NOT on its own enough to
// start compositing on it. A layout must also have been measured against THAT
// file, or heroOption passes it over.
const SportPackageCard = ({
  packageItem, to, currentSeason, schoolId, customer, CardComponent,
}) => {
  const { data: options } = usePackageOptions(packageItem.id, schoolId);
  const hero = heroOption(packageItem.id, options);
  // Unconditional: `hero` arrives asynchronously and this runs hooks, so gating
  // the call would change the hook count between renders.
  const composited = AbsolutePosImgs(customer, packageItem.id, hero || NO_OPTION);

  if (!hero) {
    return <CardComponent packageItem={packageItem} to={to} currentSeason={currentSeason} />;
  }

  return (
    <CardComponent
      packageItem={{ ...packageItem, bannerUrl: hero.imageUrl, useNewPackageBuilder: true }}
      to={to}
      currentSeason={currentSeason}
      overlay={composited}
    />
  );
};

SportPackageCard.propTypes = {
  packageItem: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  to: PropTypes.string.isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  CardComponent: PropTypes.elementType.isRequired,
};

const PackageSelection = () => {
  const { pid, senior, ui } = useQueryString()
  // ?ui=v2 previews the redesigned card layout (senior-sitting-style). Only
  // affects rendering -- data fetch, gating, click behavior are all untouched.
  // Rolls out per-URL for now; a per-school gate lands once we're happy with
  // the layout.
  const useV2Layout = ui === 'v2'
  const { customer, setCustomer } = useContext(CustomerContext)
  const { currentStudent } = customer
  const [packages, setPackages] = useState([])
  const [isLoadingPackages, setIsLoadingPackages] = useState(true)
  const [studentImages, setStudentImages] = useState([])
  // The cards' destinations are computed during render, and one of them
  // (sport team-photo-only) is answered by this student's image. Without its
  // own flag the grid paints first and a card points at the favorites gallery
  // until the fetch lands -- a customer who clicks fast gets the page this
  // feature exists to avoid. fetchingPastImages cannot stand in for it: its
  // effect also runs on mount with an empty list and clears immediately.
  const [isLoadingStudentImages, setIsLoadingStudentImages] = useState(true)
  const [displayPastImages, setDisplayPastImages] = useState(false)
  const [fetchingPastImages, setFetchingPastImages] = useState(true)
  const [currentStudentImage, setCurrentStudentImage] = useState()
  const { fetching, data: poseData } = usePoses(currentStudent.id, packages.map((pack) => pack.id))
  const { fetching: isFetchingPreviousOrders, data: hasPreviousOrders } = getPreviousOrders(currentStudent.id)
  const [isUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const [isOrderingAhead] = useLocalStorage('orderingAhead')
  const [currentSeason] = useLocalStorage(`currentSeason-${currentStudent.id}`)
  const { data: pastImagesQuickOrderPricelist } = usePastImagesQuickOrderPricelist(customer.currentSchool && customer.currentSchool.id)
  const showPastImagesCard = !!pastImagesQuickOrderPricelist && displayPastImages && !isOrderingAhead && !pid && !senior

  const pricelistSort = (a, b) => {
    if (hasPreviousOrders) {
      if (PURCHASED_CATEGORY_WEIGHTS[a.category] < PURCHASED_CATEGORY_WEIGHTS[b.category]) return -1;
    } else {
      if (CATEGORY_WEIGHTS[a.category] < CATEGORY_WEIGHTS[b.category]) return -1;
    }

    return 1;
  }

  useEffect(() => {
    StudentImage
      // Was this player only ever in the team picture, and which pose is it?
      // Both answered server-side -- see StudentImage#sport_team_photo_only?.
      // Asked here because this screen already loads the student's images and
      // is the screen that decides where the card goes.
      .selectExtra(['sportTeamPhotoOnly', 'sportTeamPoseId'])
      .where({ studentId: currentStudent.id })
      .all()
      .then((response) => {
        setStudentImages(response.data)
        setCurrentStudentImage(response.data.find(image => image.folder === currentSeason))
      })
      .finally(() => setIsLoadingStudentImages(false))
  }, [])

  useEffect(() => {
    const preorderParam = isOrderingAhead ? { season: { match: 'fall2023' } } : {}
    // Only the unqualified picker narrows — see the note on the filter below.
    const sportSeasonParam = (!pid && !senior && currentSeason)
      ? { forSportSeason: currentSeason }
      : {}
    Package
      .includes({ backgroundSet: 'backgrounds' })
      .where({ schoolId: customer.currentSchool.id })
      .where({ has_prices_for_school: customer.currentSchool.id })
      .where(preorderParam)
      .where({ notPastImages: true })
      .where({ shouldShowAppointments: false })
      // ⚠️ SPORTS CUSTOMERS WERE SHOWN EVERYTHING. Measured in production
      // 2026-09-08 (issue #5353): with no season filter here, a parent arriving
      // with a sports access code got 7 to 14 pricelists, exactly ONE of them
      // sports — Sonora, Davis and Mossdale each show 14, alongside Senior
      // Portraits, Graduation and Gift Package. The sport pricelists carry the
      // right package_poses all along; customers never reached them.
      //
      // The rule lives in Api::PackageResource, not here, because "is this a
      // sport season" is a question the app already answered five different
      // ways — two of them wrong on real data. Passing the season and letting
      // the server decide keeps it to one answer. Non-sport seasons pass
      // through untouched, and the filter never returns an empty picker.
      //
      // ⚠️ NOT WHEN `pid` OR `senior` IS SET. Those branches below filter the
      // RESPONSE down to one target package, so anything the server dropped
      // first is gone with no way back — the customer would land on a package
      // link and see an empty screen. `currentSeason` is a single per-student
      // localStorage slot written by whichever flow they used last, NOT "the
      // season this link is about": a senior who plays a fall sport can carry
      // `sportsfall2026` into a senior-portraits link. An explicit link always
      // wins over that guess.
      .where(sportSeasonParam)
      .all()
      .then((res) => {
        if (pid) {
          setPackages(res.data.filter((datum) => datum.id.toString() === pid.toString() || datum.category === 'gift').sort(pricelistSort))
        } else if (senior) setPackages(res.data.filter((datum) => (datum.name.toLowerCase().includes('senior') || datum.category === 'gift') && !datum.isSitting))
        else setPackages(res.data.sort(pricelistSort))
      })
      .finally(() => setIsLoadingPackages(false))
    // currentSeason joins the deps: it arrives from localStorage and the
    // packages must be re-fetched once it is known, or a sports customer keeps
    // the unfiltered list they were served first.
  }, [hasPreviousOrders, currentSeason])

  useEffect(() => {
    const folders = studentImages.map(images => images.folder).filter(folder => !!folder)
    const years = folders.map(folder => folder.substr(folder.length - 4)).map(year => parseInt(year))

    setDisplayPastImages(isUsingAccessCode && years.some(year => year < currentYear()))
    setFetchingPastImages(false)
  }, [JSON.stringify({ studentImages })])

  useEffect(() => {
    if (fetchingPastImages) return
    if (packages.length === 0 || Object.keys(poseData).length === 0) return
    if (displayPastImages) return
    // The cart is created asynchronously (CustomerContext), so it can still be null
    // when packages + poses finish loading. Guard the dereference and re-run once the
    // cart exists (customer.cart is in the deps below) instead of throwing.
    if (!customer.cart) return

    const packageItem = packages[0];
    const orderPackage = new OrderPackage({
      packageId: packageItem.id,
      cartId: customer.cart.id,
      isPreviewHidden: !isUsingAccessCode,
    })

    setCustomer({ ...customer, currentOrderPackage: orderPackage })

    // TODO: uncomment when Alex wants auto-pushing back again
    // history.push(route(packageItem));

  }, [displayPastImages, packages, poseData, fetchingPastImages, customer.cart])

  const currentYear = () => {
    const date = new Date()
    const month = date.getMonth()
    const year = date.getFullYear()

    if (month >= 6) return year + 1

    return year
  }

  const route = (packageItem) => {
    let to = ''
    // The sport player with no individual picture -- see sportTeamPictureRoute.
    // Null for everyone else, which is every other card on this screen.
    const teamPictureTo = sportTeamPictureRoute(packageItem, currentStudentImage)

    if (packageItem.category === 'gift') {
      to = routes.holiday.builder({ pid: pid });
    } else if (teamPictureTo) {
      to = teamPictureTo;
    } else if (packageItem.isSeniorPortraits && !packageItem.isSitting) {
      to = routes.seniors.packageBuilder(packageItem.id);
      // to = routes.favoritePoseSelection(packageItem.id)
    } else if (packageItem.multiple && currentSeason && poseData[packageItem.id] && poseData[packageItem.id].length > 0) {
      to = routes.elementaryMultiPoseBuilder(packageItem.id)
    } else if (currentSeason && packageItem.backgroundSet && packageItem.backgroundSet.backgrounds.length > 0) {
      to = `/background_selection/${packageItem.id}`
    } else if (packageItem.multiple && poseData[packageItem.id]) {
      to = routes.elementaryMultiPoseBuilder(packageItem.id)
    } else if (packageItem.backgroundSet && packageItem.backgroundSet.backgrounds.length > 0) {
      to = `/background_selection/${packageItem.id}`
    } else {
      to = `/option_selection/${packageItem.id}`
    }

    return to;
  }

  const renderPackages = () => {
    if (fetching || isLoadingPackages || isFetchingPreviousOrders || isLoadingStudentImages) {
      return (
        <div className={classes.spinnerContainer}>
          <Spinner color="blue" />
        </div>
      )
    }

    const CardComponent = useV2Layout ? PackageCardV2 : PackageItem

    return (
      <Grid item container direction="column" spacing={3}>
        {packages.map((packageItem) => {
          return (
            <Grid item container key={packageItem.name}>
              {/* isSport comes from the server (SportSeason.sport?), never a
                  season string parsed here — see Api::PackageResource. */}
              {SPORT_COMPOSITE_CARD && packageItem.isSport ? (
                <SportPackageCard
                  packageItem={packageItem}
                  to={route(packageItem)}
                  currentSeason={currentSeason}
                  schoolId={customer.currentSchool && customer.currentSchool.id}
                  customer={customer}
                  CardComponent={CardComponent}
                />
              ) : (
                <CardComponent
                  packageItem={packageItem}
                  to={route(packageItem)}
                  currentSeason={currentSeason}
                />
              )}
            </Grid>
          )
        })}
        {showPastImagesCard && (
          <Grid item container key="past-images-quick-order">
            <CardComponent
              packageItem={{
                id: pastImagesQuickOrderPricelist.id,
                name: pastImagesQuickOrderPricelist.name || 'Pictures from Previous Years',
                bannerUrl: pastImagesQuickOrderPricelist.bannerUrl,
                imageUrl: pastImagesQuickOrderPricelist.imageUrl || prevYearsBanner,
                useNewPackageBuilder: !!pastImagesQuickOrderPricelist.bannerUrl,
                isSeniorPortraits: false,
              }}
              to={routes.pastImages.quickOrder()}
              currentSeason={currentSeason}
            />
          </Grid>
        )}
        {false && displayPastImages && !isOrderingAhead && (
          <Grid item>
            <PreviousYears student={currentStudent} />
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <div className={styles.outerContainer}>
      <Grid container direction="column" wrap="nowrap" classes={styles.pageContainer}>
        <Grid item classes={styles.instructionsContainer}>
          <Typography
            weight="inherit"
            align="center"
            classes={styles.headerText}>
            Choose one to view packages
          </Typography>
        </Grid>
        {renderPackages()}
      </Grid>
    </div>
  )
}

export default PackageSelection
